// Chitanță (reçu d'encaissement roumain) — A6 paysage, généré côté navigateur.
//
// Extrait de views/invoicing/FirewoodInvoiceView.vue pour être partagé avec
// l'écran facture standard (views/invoicing/OperationView.vue) : une facture
// payée en espèces doit pouvoir être quittancée qu'elle vienne de la borne ou
// de la saisie normale, et les deux reçus doivent être le MÊME document.
//
// Le numéro n'est PAS imprimé : le compteur applicatif ne correspond pas au
// carnet à souches déclaré, c'est ce dernier qui fait foi. La ligne « Nr. »
// est laissée en pointillés, à remplir à la main.
import jsPDF from "jspdf";
import moment from "moment";
import { AmiriRegular } from "@/assets/fonts/Amiri-Regular-normal.js";
import { patchJsPdfRomanian } from "@/helpers/roDiacritics.js";

export default {
  methods: {
    /** Sumă în litere românești: ex. două mii trei sute patruzeci si sapte lei si 40 bani */
    amountToRomanianWords(lei, bani) {
      const units = ['', 'unu', 'două', 'trei', 'patru', 'cinci', 'sase', 'sapte', 'opt', 'nouă'];
      const tens = ['', '', 'douăzeci', 'treizeci', 'patruzeci', 'cincizeci', 'saizeci', 'saptezeci', 'optzeci', 'nouăzeci'];
      const teens = ['zece', 'unsprezece', 'douăsprezece', 'treisprezece', 'paisprezece', 'cincisprezece', 'saisprezece', 'saptesprezece', 'optsprezece', 'nouăsprezece'];
      const hundreds = ['', 'o sută', 'două sute', 'trei sute', 'patru sute', 'cinci sute', 'sase sute', 'sapte sute', 'opt sute', 'nouă sute'];
      function toWords(n) {
        if (n === 0) return 'zero';
        if (n < 10) return units[n];
        if (n < 20) return teens[n - 10];
        if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' si ' + units[n % 10] : '');
        if (n < 1000) return hundreds[Math.floor(n / 100)] + (n % 100 ? ' ' + toWords(n % 100) : '');
        if (n < 1000000) {
          const th = Math.floor(n / 1000);
          const rest = n % 1000;
          const thStr = th === 1 ? 'o mie' : (th < 20 ? toWords(th) : toWords(th)) + ' mii';
          return thStr + (rest ? ' ' + toWords(rest) : '');
        }
        const mil = Math.floor(n / 1000000);
        const rest = n % 1000000;
        const milStr = mil === 1 ? 'un milion' : toWords(mil) + ' milioane';
        return milStr + (rest ? ' ' + toWords(rest) : '');
      }
      const L = Math.floor(Number(lei) || 0);
      const B = Math.round(Number(bani) || 0);
      const leiStr = toWords(L);
      const leiLabel = L === 1 ? 'leu' : 'lei';
      const baniLabel = B === 1 ? 'ban' : 'bani';
      return leiStr + ' ' + leiLabel + (B > 0 ? ' si ' + B + ' ' + baniLabel : '');
    },
    /** Format sumă: 2347.40 -> "2.347,40" (mii cu punct, zecimale cu virgulă) */
    formatSumaChitanta(num) {
      const n = parseFloat(num);
      if (Number.isNaN(n)) return '0,00';
      const intPart = Math.floor(Math.abs(n));
      const decPart = Math.round((Math.abs(n) - intPart) * 100);
      const intStr = intPart.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      return intStr + ',' + (decPart < 10 ? '0' : '') + decPart;
    },
    async GenerateChitantaCash() {
      if (!this.user_data || !this.user_data.people || !this.user_data.people.length) {
        this.$vs.notify({ color: 'danger', title: 'Error', text: 'Missing customer data.' });
        return;
      }
      try {
        // La chitanța atteste un ENCAISSEMENT : elle doit porter le TTC réel du
        // document, celui du PDF et de l'e-factura. Les totaux sont chargés au
        // montage de l'écran, mais un clic très rapide pouvait les trouver
        // encore vides — auquel cas on les attend plutôt que d'imprimer 0.
        if (!this.documentTotals && typeof this.getProductListPdf === 'function') {
          await this.getProductListPdf();
        }
        // Meme raisonnement pour l'adresse imprimee sur le recu : elle doit
        // etre celle du document, pas un etat partiel. On ATTEND le chargement.
        // On ne REFUSE pas pour autant — un encaissement ne se bloque pas, le
        // repli « — » ci-dessous reste assume.
        if (typeof this.addressesReady === 'function') {
          await this.addressesReady();
        }
        const totalRon = Math.abs(this.docTotalTtcRon);
        const lei = Math.floor(totalRon);
        const bani = Math.round((totalRon - lei) * 100);
        const sumaFormat = this.formatSumaChitanta(totalRon);
        const sumaInLitere = this.amountToRomanianWords(lei, bani);
        const clientName = this.user_data.people[0].company_name || (this.user_data.people[0].firstname || '') + ' ' + (this.user_data.people[0].lastname || '').trim() || '—';
        const clientCif = (this.user_data.people[0].vat_number && this.user_data.people[0].vat_number.trim()) ? this.user_data.people[0].vat_number.trim() : '—';
        let clientAdresa = '—';
        if (this.addressbilling && (this.addressbilling.street || this.addressbilling.place)) {
          const parts = [this.addressbilling.street, this.addressbilling.nr, this.addressbilling.cp, this.addressbilling.place, this.addressbilling.country].filter(Boolean);
          clientAdresa = parts.join(', ') || '—';
        }
        const dataIncasare = moment().format('DD.MM.YYYY');
        const nrFactura = (this.user_data.invoice_no && this.user_data.invoice_no.trim()) ? this.user_data.invoice_no.trim() : ('Doc. ' + this.user_data.id);
        const dataReferinta = this.user_data.date ? moment(this.user_data.date).format('DD.MM.YYYY') : dataIncasare;
        const reprezentand = 'C.V. ' + nrFactura + ' / ' + dataReferinta;

        // A6 paysage: 148 x 105 mm, marges 6 mm
        const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a6' });
        // Amiri n'a pas Șș/Țț (virgule souscrite) : sans cette substitution
        // le titre s'imprime « CHITANA » et le cadre « Judeul ».
        patchJsPdfRomanian(doc);
        doc.addFileToVFS("Amiri-Regular.ttf", AmiriRegular);
        doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
        doc.setFont("Amiri", "normal");
        const pageW = 148;
        const pageH = 105;
        const margin = 6;
        const contentW = pageW - 2 * margin;
        const contentH = pageH - 2 * margin;

        // Helper: ligne pointillée horizontale (jsPDF n'a pas setLineDashPattern, on dessine des segments)
        const dottedLine = (y, xStart, xEnd) => {
          doc.setDrawColor(100, 100, 100);
          doc.setLineWidth(0.15);
          const dashLen = 1.2;
          const gapLen = 1.2;
          let x = xStart;
          while (x < xEnd) {
            const segEnd = Math.min(x + dashLen, xEnd);
            doc.line(x, y, segEnd, y);
            x += dashLen + gapLen;
          }
          doc.setDrawColor(0, 0, 0);
        };

        // —— Cadre UNITATEA (tampon) en haut à gauche: ~60% largeur, ~22% hauteur
        const frameW = contentW * 0.6;
        const frameH = contentH * 0.22;
        const frameX = margin;
        const frameY = margin;
        doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.25);
        doc.rect(frameX, frameY, frameW, frameH);
        doc.setFontSize(6);
        doc.setFont("Amiri", "normal");
        const labelX = frameX + 2;
        let lineY = frameY + 4;
        const valueX = frameX + frameW * 0.5;
        doc.text('UNITATEA:', labelX, lineY);
        doc.text('XYLO IMPACT S.R.L.', valueX, lineY);
        lineY += 3.2;
        doc.text('Nr.Ord.reg./an:', labelX, lineY);
        doc.text('J2022000979207', valueX, lineY);
        lineY += 3.2;
        doc.text('C.I.F.:', labelX, lineY);
        doc.text('RO33531190', valueX, lineY);
        lineY += 3.2;
        doc.text('Sediu:', labelX, lineY);
        doc.text('Sat Teiu, Com. Lăpugiu de Jos, Nr. 115A', valueX, lineY);
        lineY += 3.2;
        doc.text('Județul:', labelX, lineY);
        doc.text('Hunedoara', valueX, lineY);

        // "Nr." en haut à droite (petit)
        doc.setFontSize(8);

        // Titre CHITANȚA centré + "Nr." à droite sur même ligne avec pointillés.
        // Le numéro reste vide : il est repris à la main sur le carnet à souches.
        let y = frameY + frameH + 8;
        doc.setFontSize(20);
        doc.setFont("Amiri", "normal");
        doc.text('CHITANȚA', margin + contentW / 2, y, { align: 'center' });
        doc.setFontSize(10);
        doc.setFont("Amiri", "normal");
        const xNr = margin + contentW / 2 + 22;
        doc.text('Nr.', xNr, y);
        dottedLine(y + 1.5, xNr + 10, pageW - margin);

        // Data ………………… 20……
        y += 6;
        doc.setFontSize(10);
        doc.text('Data', margin, y);
        dottedLine(y + 1.5, margin + 10, pageW - margin - 14);
        doc.setFontSize(9);
        doc.text(dataIncasare, margin + 12, y);

        // Am primit de la ………………………………………
        y += 5.5;
        doc.text('Am primit de la', margin, y);
        dottedLine(y + 1.5, margin + 28, pageW - margin);
        doc.text(clientName, margin + 30, y);

        // C.I.F. ………………………… Nr.ord.reg.com./an …………………………
        y += 5.5;
        doc.text('C.I.F.', margin, y);
        const cifLineStart = margin + 14;
        const cifLineMid = contentW * 0.5 + margin;
        dottedLine(y + 1.5, cifLineStart, cifLineMid - 4);
        doc.text(clientCif, cifLineStart + 2, y);
        doc.text('Nr.ord.reg.com./an', cifLineMid, y);
        dottedLine(y + 1.5, cifLineMid + 38, pageW - margin);
        doc.text('—', cifLineMid + 40, y);

        // Adresa …………………………………………………………………………………
        y += 5.5;
        doc.text('Adresa', margin, y);
        dottedLine(y + 1.5, margin + 18, pageW - margin);
        doc.text(clientAdresa, margin + 20, y);

        // Suma de ………………………… adică ……………………………………………
        y += 5.5;
        doc.text('Suma de', margin, y);
        dottedLine(y + 1.5, margin + 22, margin + 52);
        doc.text(sumaFormat, margin + 24, y);
        doc.text('adică', margin + 55, y);
        dottedLine(y + 1.5, margin + 68, pageW - margin);
        doc.text(sumaInLitere + '.', margin + 70, y);

        // reprezentând …………………………………………………………………………
        y += 5.5;
        doc.text('reprezentând', margin, y);
        dottedLine(y + 1.5, margin + 28, pageW - margin);
        doc.text(reprezentand, margin + 30, y);

        const filename = 'Chitanta_Cash_' + (this.user_data.invoice_no || this.user_data.id);
        doc.save(filename + '.pdf');
        this.$vs.notify({ color: 'success', title: 'Receipt generated', text: 'Cash receipt document downloaded.' });
      } catch (err) {
        console.error(err);
        this.$vs.notify({ color: 'danger', title: 'Receipt error', text: err.response && err.response.data ? (err.response.data.error || err.response.data.message) : err.message });
      }
    },
  },
};
